import { createContext, useContext, useEffect, useState } from 'react';

// Design refresh (2026-05-11): primary accent is now Anthropic
// terracotta (`#D97757`); the prior amber `#d4a843` is retained as
// a "warm-gold" alternative for backwards-compat with users whose
// localStorage already has the old default.
export type AccentChoice = '#D97757' | '#d4a843' | '#2dd4bf' | '#e85d75';
export type DensityChoice = 'comfortable' | 'compact';
// Theme: 'system' (default) follows the OS preference via
// `prefers-color-scheme`. Explicit 'light' / 'dark' override.
export type ThemeChoice = 'system' | 'light' | 'dark';

export interface FosTweaks {
  accent: AccentChoice;
  density: DensityChoice;
  theme: ThemeChoice;
}

const DEFAULT_TWEAKS: FosTweaks = {
  accent: '#D97757',
  density: 'comfortable',
  theme: 'system',
};

// Bump the storage key so users who saved tweaks under the dark
// theme (which defaulted to amber `#d4a843`) get a clean slate on
// the new light theme. The old `forvisibility.tweaks.v1` blob is left
// in localStorage but ignored.
const STORAGE_KEY = 'forvisibility.tweaks.v2';

export const ACCENT_PRESETS: Record<AccentChoice, { hover: string; muted: string }> = {
  '#D97757': { hover: '#A84E2E', muted: '#B9663E' },
  '#d4a843': { hover: '#deb54e', muted: '#a88832' },
  '#2dd4bf': { hover: '#5eead4', muted: '#14b8a6' },
  '#e85d75': { hover: '#f087a0', muted: '#c24060' },
};

function loadTweaks(): FosTweaks {
  if (typeof window === 'undefined') return DEFAULT_TWEAKS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_TWEAKS;
    const parsed = JSON.parse(raw) as Partial<FosTweaks>;
    // Keep only known keys: older blobs may still carry a retired `aiMode`
    // (the global mode toggle was removed, fl-db9).
    return {
      accent: parsed.accent ?? DEFAULT_TWEAKS.accent,
      density: parsed.density ?? DEFAULT_TWEAKS.density,
      theme: parsed.theme ?? DEFAULT_TWEAKS.theme,
    };
  } catch {
    return DEFAULT_TWEAKS;
  }
}

function saveTweaks(t: FosTweaks) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(t));
  } catch {
    /* ignore quota errors */
  }
}

/** Apply the theme by toggling `html.dark`. Pure side-effect helper —
 *  callers also re-invoke this on the `(prefers-color-scheme: dark)`
 *  media-query change event so the 'system' setting tracks OS-level
 *  flips live. */
function applyTheme(theme: ThemeChoice) {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  const prefersDark =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;
  const isDark = theme === 'dark' || (theme === 'system' && prefersDark);
  document.documentElement.classList.toggle('dark', isDark);
}

/** Hook giving the current tweaks + an updater. Persists to localStorage and
 *  applies side effects (CSS vars + body density class + html.dark theme)
 *  whenever values change. Used internally by `FosAppShell`; downstream
 *  components should consume the React context (`useFosTweaksValue`)
 *  instead. */
export function useFosTweaks() {
  const [tweaks, setTweaksState] = useState<FosTweaks>(() => loadTweaks());

  useEffect(() => {
    saveTweaks(tweaks);
    if (typeof document === 'undefined') return;

    const root = document.documentElement;
    const preset = ACCENT_PRESETS[tweaks.accent];
    root.style.setProperty('--fos-accent', tweaks.accent);
    root.style.setProperty('--fos-accent-hover', preset.hover);
    root.style.setProperty('--fos-accent-muted', preset.muted);
    root.style.setProperty('--fos-accent-bg', `${tweaks.accent}14`);
    root.style.setProperty('--fos-accent-bg-strong', `${tweaks.accent}26`);
    root.style.setProperty('--fos-accent-border', `${tweaks.accent}33`);

    document.body.classList.toggle('density-compact', tweaks.density === 'compact');
    document.body.classList.toggle('density-comfortable', tweaks.density !== 'compact');

    applyTheme(tweaks.theme);
  }, [tweaks]);

  // Track OS theme changes so 'system' mode flips live when the user
  // changes their macOS / Windows / browser theme.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      if (tweaks.theme === 'system') applyTheme('system');
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [tweaks.theme]);

  const setTweak = <K extends keyof FosTweaks>(key: K, value: FosTweaks[K]) => {
    setTweaksState((prev) => ({ ...prev, [key]: value }));
  };

  return { tweaks, setTweak } as const;
}

export interface FosTweaksContextValue {
  tweaks: FosTweaks;
  setTweak: <K extends keyof FosTweaks>(key: K, value: FosTweaks[K]) => void;
}

export const FosTweaksContext = createContext<FosTweaksContextValue | null>(null);

export function useFosTweaksValue(): FosTweaksContextValue {
  const ctx = useContext(FosTweaksContext);
  if (!ctx) {
    throw new Error('useFosTweaksValue must be used inside <FosAppShell>');
  }
  return ctx;
}
